import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { Typography } from '@saucelabs/ds-typography';
import { Button } from '@saucelabs/ds-button';
import { Tooltip } from '@saucelabs/ds-tooltip';
import { InputText } from '@saucelabs/ds-inputs';
import { IconInformationOutline, IconStatusFailColor, IconStatusPassColor } from '@saucelabs/ds-icons';
import { InlineAlert } from '@saucelabs/ds-alert';

import { ValueOf } from '../../../globalTypes';
import { trackEvent, MANUAL_EVENTS } from '../../../tracking';
import { selectors as testConfigurationSelectors } from '../../testConfiguration';
import selectors from '../selectors';
import actions from '../actions';
import utils from '../utils';
import operations from '../operations';

const UPDATE_RESPONSE_STATUS = {
  FULFILLED: 'fulfilled',
  REJECTED: 'rejected',
  UNKNOWN: 'unknown',
} as const;
type UPDATE_RESPONSE_STATUS = ValueOf<typeof UPDATE_RESPONSE_STATUS>;

const ChangeTestData: React.FC = () => {
  const dispatch = useDispatch<ReduxDispatcher>();
  const deviceType = useSelector(testConfigurationSelectors.deviceTypeSelector);
  const testType = useSelector(testConfigurationSelectors.testTypeSelector);
  const remoteTestName = useSelector(selectors.remoteTestNameSelector);
  const testId = useSelector(selectors.testIdSelector);
  const [name, setName] = useState(remoteTestName);
  const [passed, setPassed] = useState<boolean | undefined>(undefined);
  const [responseStatus, setResponseStatus] = useState<UPDATE_RESPONSE_STATUS>(
    UPDATE_RESPONSE_STATUS.UNKNOWN,
  );

  const updateRemoteTestData = () => {
    trackEvent(MANUAL_EVENTS.END_SESSION_SAVE_BUTTON, {
      deviceType,
      passed: passed === undefined ? null : passed,
      input: name,
      inputChanged: name !== remoteTestName,
      testType,
    });

    void dispatch(operations.updateTestDetails({ name, passed }))
      .then(({ meta }) => {
        if ((meta.requestStatus === UPDATE_RESPONSE_STATUS.FULFILLED)) {
          return dispatch(push(`/tests/${testId}`));
        }

        return setResponseStatus(meta.requestStatus);
      });
  };

  const updateRemoteTestDataOnEnter = ({ key }) => {
    if (key === 'Enter' && name) {
      updateRemoteTestData();
    }
  };

  useEffect(() => () => (
    void dispatch(actions.resetTestDetails())
  ), [dispatch]);

  useEffect(() => setName(remoteTestName), [remoteTestName]);

  return (
    <div className="w-full pb-5 mb-5 border-0 border-t border-b border-gray-300 border-solid">
      <div className="w-full p-3 pb-0 flex flex-col">
        <div className="flex flex-row items-center mt-2">
          <Typography
            className="text-high-emphasis font-semibold"
            component="span"
            variant="body-large"
          >
            What was the outcome of your test?
          </Typography>
          <Tooltip
            placement="top"
            title="If not set, a system status will be used"
          >
            <span className="flex items-center">
              <IconInformationOutline className="w-2 h-2 ml-1 fill-current" />
            </span>
          </Tooltip>
        </div>
        <div className="flex flex-row mt-0.5">
          <Button
            className="w-3/4 mr-1 justify-center"
            color="secondary"
            isActive={passed === true}
            onPress={() => {
              if (passed) {
                setPassed(undefined);
              } else {
                setPassed(true);
              }
            }}
            prefixIcon={<IconStatusPassColor />}
            variant="outline"
          >
            Passed
          </Button>
          <Button
            className="w-3/4 ml-1 justify-center"
            color="secondary"
            isActive={passed === false}
            onPress={() => {
              if (passed === false) {
                setPassed(undefined);
              } else {
                setPassed(false);
              }
            }}
            prefixIcon={<IconStatusFailColor />}
            variant="outline"
          >
            Failed
          </Button>
        </div>
        <InputText
          autoFocus
          className="mt-5"
          data-qa="session-common-change-test-name-input"
          description={(!name ?
            'The default test name will be used if this field is left blank' :
            ''
          )}
          label={(
            <>
              <Typography
                className="text-high-emphasis font-semibold"
                variant="body-large"
              >
                What did you test? Name your test session.
              </Typography>

              <Tooltip
                placement="top"
                title="Max. 255 chars, no emoji"
              >
                <span className="flex items-center">
                  <IconInformationOutline className="w-2 h-2 ml-1 fill-current" />
                </span>
              </Tooltip>
            </>
          )}
          onChange={(newName) => {
            if (responseStatus !== UPDATE_RESPONSE_STATUS.UNKNOWN) {
              setResponseStatus(UPDATE_RESPONSE_STATUS.UNKNOWN);
            }
            setName(utils.sanitizeTestNameInput(newName));
          }}
          onKeyDown={updateRemoteTestDataOnEnter}
          placeholder="e.g. Bug found (JIRA-123)"
          value={name}
        />

        <Button
          className="mt-5 ml-auto"
          data-qa="session-common-change-test-data-submit"
          disabled={!name}
          onPress={updateRemoteTestData}
        >
          Save
        </Button>
      </div>

      {responseStatus === UPDATE_RESPONSE_STATUS.REJECTED && (
        <InlineAlert
          className="mt-6 text-left"
          color="danger"
          data-qa="session-common-change-test-data-failure"
          onDismiss={() => setResponseStatus(UPDATE_RESPONSE_STATUS.UNKNOWN)}
        >
          <Typography variant="body-large">
            Failed to update the test data, please try again.
          </Typography>
        </InlineAlert>
      )}
    </div>
  );
};

export default ChangeTestData;
